import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getGkAirTrackingConsentClientSide, setGkAirTrackingConsentClientSide } from '~/shell/cookies';
import { getLogger } from '~/logging/logger.ts';

// Mirrors the Usercentrics tracking consent into the `tg_gkair-consent` cookie so the
// server-side GK AIR calls honour it. UC_UI is loaded at runtime via Tealium and may be absent
// (Tealium disabled / bot / local dev); in that case this component is a graceful no-op.

// Usercentrics technical service id of the GK AIR service (the `s.id` from getServicesBaseInfo()).
const GK_AIR_UC_SERVICE_ID = 'ess_H8_2okW3hZ';
const CONSENT_EVENT = 'UC_UI_CMP_EVENT';
const POLL_INTERVAL_MS = 250;
const MAX_POLL_ATTEMPTS = 60; // ~15s, then give up (UC never loaded)
const CHANGE_SYNC_DELAY_MS = 300; // UC updates getServicesBaseInfo() shortly after the change event

type UcUi = {
  isInitialized?: () => boolean;
  getServicesBaseInfo?: () => { id?: string; consent?: { status?: boolean } }[];
};

export function GkAirConsentSync() {
  const queryClient = useQueryClient();
  const log = getLogger('gk-air-consent-sync');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const getUcUi = () => (window as unknown as { UC_UI?: UcUi }).UC_UI;

    const sync = () => {
      try {
        const services = getUcUi()?.getServicesBaseInfo?.();
        if (!services) return; // UC not ready
        const service = services.find(s => s.id === GK_AIR_UC_SERVICE_ID);
        if (!service) {
          log.warn(
            `GK AIR service '${GK_AIR_UC_SERVICE_ID}' not found in Usercentrics services; consent sync disabled`
          );
          return;
        }
        const live = service.consent?.status === true;
        const stored = getGkAirTrackingConsentClientSide();
        // An absent cookie already means "no consent" server-side, so treat it like false — this skips
        // both the cookie write and the refetch for non-consenting first visits.
        if ((stored ?? false) !== live) {
          setGkAirTrackingConsentClientSide(live);
          log.info(`GK AIR tracking consent changed (${String(stored)} -> ${live}); refetching recommendations`);
          queryClient.invalidateQueries({ queryKey: ['recommendedArticles'] });
        }
      } catch (err) {
        log.error('Failed to sync GK AIR tracking consent', err);
      }
    };

    // Re-sync when the user saves a consent choice (delayed so UC has persisted the new status).
    let changeTimer: ReturnType<typeof setTimeout> | undefined;
    const onConsentChange = () => {
      clearTimeout(changeTimer);
      changeTimer = setTimeout(sync, CHANGE_SYNC_DELAY_MS);
    };
    window.addEventListener(CONSENT_EVENT, onConsentChange);

    // Initial sync once UC is initialized. UC loads async via Tealium, so poll briefly until
    // isInitialized() (which guarantees the services are readable), then stop.
    let attempts = 0;
    const poll = setInterval(() => {
      if (getUcUi()?.isInitialized?.() === true) {
        clearInterval(poll);
        sync();
      } else if (++attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(poll);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(poll);
      clearTimeout(changeTimer);
      window.removeEventListener(CONSENT_EVENT, onConsentChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
